import { DUPE_API_URL } from '@dupe/common/constants'
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import type { LibraryCategoryFilter } from '~/src/components/library/libraryModel'
import fetcher from '~/src/lib/fetcher'
import {
  UnifiedRecentItemType,
  type RecentsResponse,
  type UnifiedRecentItem,
} from './useFetchRecents'

const DEFAULT_TYPES: UnifiedRecentItemType[] = [
  UnifiedRecentItemType.Dupe,
  UnifiedRecentItemType.Chat,
]

interface Props {
  limit?: number
  enabled?: boolean
  types?: UnifiedRecentItemType[]
  /** Only recents the api files under this category (`?category=`). */
  category?: LibraryCategoryFilter
}

const useFetchRecentsInfinite = ({
  limit = 10,
  enabled,
  types = DEFAULT_TYPES,
  category,
}: Props) => {
  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const fetchUrl = `${DUPE_API_URL}/api/recents`

  const id = authUser?.uid || tempUserId
  const loading = loadingAuthUser
  // Sort + dedupe so caller-ordering or accidental duplicates don't produce
  // different cache keys (or query strings) for the same logical set.
  // Empty arrays fall back to the default so we never send `types=`.
  const typesParam = Array.from(
    new Set((types.length > 0 ? types : DEFAULT_TYPES).slice().sort())
  ).join(',')

  const query = useInfiniteQuery<
    RecentsResponse,
    Error,
    InfiniteData<RecentsResponse>,
    (string | number | undefined)[],
    number
  >({
    queryKey: [
      'recents-infinite',
      id,
      limit,
      authUser?.uid,
      tempUserId,
      typesParam,
      // Last, so `['recents-infinite']` invalidations still reach it.
      ...(category ? [category] : []),
    ],
    queryFn: ({ pageParam = 0 }) =>
      fetcher<RecentsResponse>(fetchUrl, {
        method: 'GET',
        dataOrParams: {
          limit,
          offset: pageParam,
          types: typesParam,
          ...(category ? { category } : {}),
        },
        tempUserId,
        authUser,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // If hasMore is false, we're done
      if (!lastPage.hasMore) {
        return undefined
      }

      // Calculate the next offset based on all items loaded so far
      const currentTotalItems = allPages.reduce(
        (acc, page) => acc + (page.recents?.length ?? 0),
        0
      )

      // Return the next offset
      return currentTotalItems
    },
    initialPageParam: 0,
    enabled: !!id && enabled && !loading,
    // Prevent multiple requests on window focus by increasing stale time
    // This means data won't be considered stale for 5 minutes
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep cache for 10 minutes (must be >= staleTime)
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    // Only refetch if data is actually stale
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Prevent refetching if data is still fresh
    refetchInterval: false,
  })

  // Extract the flattened recents array and hasMore from the last page
  const recents = query.data?.pages?.flatMap((page) => page.recents) ?? []
  const hasMore =
    query.data?.pages?.[query.data.pages.length - 1]?.hasMore ?? false

  return {
    ...query,
    data: recents as UnifiedRecentItem[],
    hasMore,
  }
}

export default useFetchRecentsInfinite
